/**
 * Budget - per-account budgeted amounts for a fiscal period, and the
 * budget-vs-actual comparison. The backend (routers/budget_router.py,
 * services/budget_service.py) was already complete when this session
 * audited the strategic-reporting layer; this page is the missing
 * frontend that makes it actually reachable.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import api from '../utils/api';
import { CircleNotch } from '@phosphor-icons/react';
import './BudgetPage.css';

function BudgetPage() {
  const [periods, setPeriods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [vsActual, setVsActual] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ gl_account_id: '', budgeted_amount: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/api/fiscal-periods/'),
      api.get('/api/chart-of-accounts'),
    ]).then(([p, a]) => {
      setPeriods(Array.isArray(p) ? p : []);
      setAccounts(Array.isArray(a) ? a : []);
    }).catch((error) => toast.error('Failed to load reference data: ' + error.message));
  }, []);

  const load = useCallback(async () => {
    if (!periodId) return;
    setLoading(true);
    try {
      const [b, v] = await Promise.all([
        api.get('/api/budgets', { params: { fiscal_period_id: periodId } }),
        api.get(`/api/budgets/vs-actual/${periodId}`),
      ]);
      setBudgets(b || []);
      setVsActual(v || []);
    } catch (error) {
      toast.error('Failed to load budget: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => { load(); }, [load]);

  const accountName = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.account_code} — ${a.account_name}` : id;
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.gl_account_id || !form.budgeted_amount) { toast.error('Account and amount are required'); return; }
    setSubmitting(true);
    try {
      await api.post('/api/budgets', {
        fiscal_period_id: periodId, gl_account_id: form.gl_account_id,
        budgeted_amount: parseFloat(form.budgeted_amount), notes: form.notes || null,
      });
      toast.success('Budget line created');
      setShowCreate(false);
      setForm({ gl_account_id: '', budgeted_amount: '', notes: '' });
      load();
    } catch (error) {
      toast.error('Failed to create budget line: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/api/budgets/${id}`);
      toast.success('Budget line deleted');
      load();
    } catch (error) {
      toast.error('Failed to delete: ' + error.message);
    }
  };

  return (
    <Layout>
      <div className="page budget-page">
        <div className="page-header">
          <div>
            <h1>Budget</h1>
            <p>Per-account budgeted amounts for a fiscal period, compared against actual posted activity</p>
          </div>
        </div>

        <div className="form-group bp-inline-filter">
          <label>Fiscal Period</label>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            <option value="">Select…</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.period_name}</option>)}
          </select>
        </div>

        {periods.length === 0 && <p className="bp-hint">No fiscal periods exist yet — create one under Finance &gt; Fiscal Periods first.</p>}

        {periodId && (
          <>
            <div className="bp-panel-header">
              <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Add Budget Line</button>
            </div>

            {loading ? (
              <div className="loading"><CircleNotch size={28} weight="bold" /> Loading...</div>
            ) : (
              <>
                <h3>Budget Lines</h3>
                {budgets.length === 0 ? <p className="empty">No budget lines for this period yet</p> : (
                  <div className="bp-table">
                    <table>
                      <thead><tr><th>Account</th><th>Budgeted Amount</th><th>Notes</th><th></th></tr></thead>
                      <tbody>
                        {budgets.map((b) => (
                          <tr key={b.id}>
                            <td>{accountName(b.gl_account_id)}</td>
                            <td>GHS {Number(b.budgeted_amount).toLocaleString()}</td>
                            <td>{b.notes || '—'}</td>
                            <td><button className="btn btn-sm btn-danger" onClick={() => remove(b.id)}>Delete</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h3 className="bp-mt">Budget vs Actual</h3>
                {vsActual.length === 0 ? <p className="empty">No comparison data yet</p> : (
                  <div className="bp-table">
                    <table>
                      <thead><tr><th>Account</th><th>Budgeted</th><th>Actual</th><th>Variance</th><th>Variance %</th></tr></thead>
                      <tbody>
                        {vsActual.map((v) => (
                          <tr key={v.gl_account_id}>
                            <td>{v.account_code} — {v.account_name}</td>
                            <td>GHS {Number(v.budgeted_amount).toLocaleString()}</td>
                            <td>GHS {Number(v.actual_amount).toLocaleString()}</td>
                            <td className={Number(v.variance) > 0 ? 'bp-over' : Number(v.variance) < 0 ? 'bp-under' : ''}>GHS {Number(v.variance).toLocaleString()}</td>
                            <td>{v.variance_percentage != null ? `${v.variance_percentage}%` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {showCreate && (
          <div className="modal-overlay" onClick={() => setShowCreate(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header"><h2>Add Budget Line</h2><button className="btn-close" onClick={() => setShowCreate(false)}>×</button></div>
              <form onSubmit={submitCreate}>
                <div className="form-group">
                  <label>GL Account *</label>
                  <select value={form.gl_account_id} onChange={(e) => setForm({ ...form, gl_account_id: e.target.value })} required>
                    <option value="">Select…</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.account_code} — {a.account_name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Budgeted Amount *</label><input type="number" min="0" step="0.01" value={form.budgeted_amount} onChange={(e) => setForm({ ...form, budgeted_amount: e.target.value })} required /></div>
                <div className="form-group"><label>Notes</label><textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Add'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BudgetPage;
